import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './FullSpecsSection.module.scss';
import classNames from 'classnames';
import { ReactComponent as DownArrow } from '../../../../../assets/images/icons/arrow_down.svg';
import { ReactComponent as UpArrow } from '../../../../../assets/images/icons/arrow_up.svg';
import {
  miniFullSpecsExpand,
  miniFullSpecsCollapse,
  series3CollarFullSpecsExpand,
  series3CollarFullSpecsCollapse,
} from '../../../../../lib/analytics/events';
import { FiCountry, FiRegion, useLocale } from '../../../../../contexts/LocaleContext';

const Series3Specs = () => {
  const { t } = useTranslation();
  const { region, country } = useLocale();
  return (
    <>
      <div className={styles.specsSection}>
        <div className={styles.specsTitle}>{t('whats_in_the_box')}</div>
        <div className={styles.specItem}>{t('series_3_plus_device')}</div>
        <div className={styles.specItem}>{t('collar')}</div>
        <div className={styles.specItem}>{t('charging_wifi_base')}</div>
        <div className={styles.specItem}>{t('cord')}</div>
      </div>

      <div className={styles.specsSection}>
        <div className={styles.specsTitle}>{t('hardware_no_colon')}</div>
        <div className={styles.specItem}>{t('device_measurements_81_5mm_x_30_3mm_x_11_5mm')}</div>
        <div className={styles.specItem}>{t('device_weight_47_grams')}</div>
        <div className={styles.specItem}>{t('ip68_66k_tested_in_salt_water_up_to_50_meters_no_period')}</div>
        <div className={styles.specItem}>
          {t(region === FiRegion.EU ? '220kg_plus_resistance' : '500lbs_plus_resistance')}
        </div>
        <div className={styles.specItem}>{t('wireless_charging')}</div>
        <div className={styles.specItem}>{t('custom_light_for_night_tracking')}</div>
        <div className={styles.specItem}>{t('up_to_3_months_of_battery_life')}</div>
        {region === FiRegion.NA ? (
          <>
            <div className={styles.specItem}>{t('att_lte_m_cellular_network')}</div>
            <div className={styles.specItem}>{t('roaming_in_the_us_canada_and_mexico')}</div>
          </>
        ) : region === FiRegion.EU ? (
          <>
            <div className={styles.specItem}>{t('o2_lte_m_cellular_network')}</div>
            {country === FiCountry.GB && <div className={styles.specItem}>{t('british_cellular_supported')}</div>}
          </>
        ) : null}
      </div>

      <div className={styles.specsSection}>
        <div className={styles.specsTitle}>{t('app_features')}</div>
        <div className={styles.specItem}>{t('24_7_escape_alerts')}</div>
        <div className={styles.specItem}>{t('lost_mode_live_tracking')}</div>
        <div className={styles.specItem}>{t('walk_history')}</div>
        <div className={styles.specItem}>{t('activity_tracking')}</div>
        <div className={styles.specItem}>{t('sleep_tracking')}</div>
        <div className={styles.specItem}>{t('ai_behavior_detection')}</div>
        <div className={styles.specItem}>{t('apple_watch_integration_lowercase')}</div>
        <div className={styles.specItem}>{t('vet_health_report')}</div>
        <div className={styles.specItem}>{t('ai_smart_document_organizer')}</div>
        <div className={styles.specItem}>{t('unlimited_users')}</div>
      </div>
    </>
  );
};

const MiniSpecs = () => {
  const { t } = useTranslation();

  return (
    <>
      <div className={styles.specsSection}>
        <div className={styles.specsTitle}>{t('whats_in_the_box')}</div>
        <div className={styles.specItem}>{t('mini_device')}</div>
        <div className={styles.specItem}>{t('3_case_options_compatible_with_all_harnesses_collars')}</div>
        <div className={styles.specItem}>{t('charging_cord')}</div>
      </div>

      <div className={styles.specsSection}>
        <div className={styles.specsTitle}>{t('hardware_no_colon')}</div>
        <div className={styles.specItem}>{t('device_measurements_42mm_x_30mm_x_12_5mm')}</div>
        <div className={styles.specItem}>{t('device_weight_16_grams')}</div>
        <div className={styles.specItem}>{t('ip68_tested_in_salt_water_no_dash')}</div>
        <div className={styles.specItem}>{t('attachable_to_collars_and_harnesses')}</div>
        <div className={styles.specItem}>{t('up_to_2_months_of_battery_life')}</div>
        <div className={styles.specItem}>{t('verizon_lte_m_cellular_network')}</div>
        <div className={styles.specItem}>{t('usb_c_charging')}</div>
      </div>

      <div className={styles.specsSection}>
        <div className={styles.specsTitle}>{t('app_features')}</div>
        <div className={styles.specItem}>{t('24_7_escape_alerts')}</div>
        <div className={styles.specItem}>{t('lost_mode_live_tracking')}</div>
        <div className={styles.specItem}>{t('outside_time_history')}</div>
        <div className={styles.specItem}>{t('activity_tracking')}</div>
        <div className={styles.specItem}>{t('sleep_tracking')}</div>
        <div className={styles.specItem}>{t('apple_watch_integration_lowercase')}</div>
        <div className={styles.specItem}>{t('vet_health_report')}</div>
        <div className={styles.specItem}>{t('ai_smart_document_organizer')}</div>
        <div className={styles.specItem}>{t('unlimited_users')}</div>
      </div>
    </>
  );
};

export const FullSpecsSection = ({ isMini }: { isMini: boolean }) => {
  const { t } = useTranslation();
  const [showDetails, setShowDetails] = useState(false);
  const onToggle = () => {
    setShowDetails((s) => {
      const willOpen = !s;
      if (isMini) {
        if (willOpen) {
          miniFullSpecsExpand();
        } else {
          miniFullSpecsCollapse();
        }
      } else {
        if (willOpen) {
          series3CollarFullSpecsExpand();
        } else {
          series3CollarFullSpecsCollapse();
        }
      }
      return willOpen;
    });
  };

  return (
    <div className={classNames(styles.detailsSection, { [styles.detailsSectionOpen]: showDetails })}>
      <button type="button" className={styles.detailsToggle} aria-expanded={showDetails} onClick={onToggle}>
        {showDetails ? (
          <div className={styles.closeContainer}>
            {t('hide_full_specs')} <UpArrow />
          </div>
        ) : (
          <div className={styles.openContainer}>
            {t('show_full_specs')} <DownArrow />
          </div>
        )}
      </button>
      <div className={styles.detailsContent} aria-hidden={!showDetails}>
        {isMini ? <MiniSpecs /> : <Series3Specs />}
      </div>
    </div>
  );
};
